/**
 * LegalSection - Displays terms acceptance status in user settings
 *
 * Shows:
 * - Current accepted version
 * - Acceptance date
 * - Links to view full terms and privacy policy
 * - Link to view acceptance history (if available)
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getUserAcceptanceHistory } from '../services/termsService';

export default function LegalSection() {
  const { userProfile, termsConfig } = useAuth();
  const [acceptanceHistory, setAcceptanceHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  // Load acceptance history when expanded
  useEffect(() => {
    const loadHistory = async () => {
      if (showHistory && userProfile?.uid) {
        setLoadingHistory(true);
        try {
          const history = await getUserAcceptanceHistory(userProfile.uid);
          setAcceptanceHistory(history);
        } catch (error) {
          console.error('Error loading acceptance history:', error);
        } finally {
          setLoadingHistory(false);
        }
      }
    };

    loadHistory();
  }, [showHistory, userProfile?.uid]);

  const formatDate = (dateValue) => {
    if (!dateValue) return 'Unknown';

    // Handle Firestore Timestamp
    const date = dateValue.toDate ? dateValue.toDate() : new Date(dateValue);

    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isUpToDate = (userVersion, requiredVersion) => {
    if (!userVersion || !requiredVersion) return false;

    const userParts = userVersion.split('.').map(Number);
    const requiredParts = requiredVersion.split('.').map(Number);

    for (let i = 0; i < Math.max(userParts.length, requiredParts.length); i++) {
      const userPart = userParts[i] || 0;
      const requiredPart = requiredParts[i] || 0;
      if (userPart < requiredPart) return false;
      if (userPart > requiredPart) return true;
    }
    return true;
  };

  const termsUpToDate = isUpToDate(
    userProfile?.termsAcceptedVersion,
    termsConfig?.requiredVersion
  );
  const privacyUpToDate = isUpToDate(
    userProfile?.privacyAcceptedVersion,
    termsConfig?.privacyVersion
  );

  return (
    <div className="bg-white dark:bg-[#1c1c1e] rounded-xl p-5 mb-4 shadow-sm border border-[#d2d2d7] dark:border-[#2c2c2e]">
      <h2 className="text-lg font-semibold text-[#1d1d1f] dark:text-white mb-4">
        Legal Agreements
      </h2>

      <div className="space-y-4">
        {/* Terms of Service */}
        <div className="p-3 bg-[#f5f5f7] dark:bg-[#2c2c2e] rounded-lg">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h3 className="font-medium text-[#1d1d1f] dark:text-white text-sm">
                  Terms of Service
                </h3>
                {termsUpToDate ? (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                    <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Up to date
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">
                    Update available
                  </span>
                )}
              </div>
              <p className="text-xs text-[#6e6e73] dark:text-[#86868b] mt-1">
                Version {userProfile?.termsAcceptedVersion || 'Not accepted'}
                {userProfile?.termsAcceptedAt && (
                  <> &middot; Accepted {formatDate(userProfile.termsAcceptedAt)}</>
                )}
              </p>
            </div>
            <Link
              to="/terms"
              target="_blank"
              className="ml-3 text-[#06c] hover:underline text-xs font-medium whitespace-nowrap"
            >
              View Terms
            </Link>
          </div>
        </div>

        {/* Privacy Policy */}
        <div className="p-3 bg-[#f5f5f7] dark:bg-[#2c2c2e] rounded-lg">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h3 className="font-medium text-[#1d1d1f] dark:text-white text-sm">
                  Privacy Policy
                </h3>
                {privacyUpToDate ? (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                    <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Up to date
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">
                    Update available
                  </span>
                )}
              </div>
              <p className="text-xs text-[#6e6e73] dark:text-[#86868b] mt-1">
                Version {userProfile?.privacyAcceptedVersion || 'Not accepted'}
                {userProfile?.privacyAcceptedAt && (
                  <> &middot; Accepted {formatDate(userProfile.privacyAcceptedAt)}</>
                )}
              </p>
            </div>
            <Link
              to="/privacy"
              target="_blank"
              className="ml-3 text-[#06c] hover:underline text-xs font-medium whitespace-nowrap"
            >
              View Policy
            </Link>
          </div>
        </div>

        {/* Acceptance History Toggle */}
        <div className="pt-2">
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="flex items-center gap-2 text-xs text-[#06c] hover:underline font-medium"
          >
            <svg
              className={`w-4 h-4 transition-transform ${showHistory ? 'rotate-90' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            {showHistory ? 'Hide' : 'View'} Acceptance History
          </button>

          {showHistory && (
            <div className="mt-3 p-3 bg-[#f5f5f7] dark:bg-[#2c2c2e] rounded-lg">
              {loadingHistory ? (
                <div className="flex items-center justify-center py-4">
                  <div className="w-5 h-5 border-2 border-[#06c]/30 border-t-[#06c] rounded-full animate-spin" />
                </div>
              ) : acceptanceHistory.length === 0 ? (
                <p className="text-xs text-[#6e6e73] dark:text-[#86868b] text-center py-2">
                  No acceptance history available
                </p>
              ) : (
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {acceptanceHistory.map((record, index) => (
                    <div
                      key={record.id || index}
                      className="flex items-center justify-between py-1.5 border-b border-[#d2d2d7]/50 dark:border-[#3c3c3e] last:border-0"
                    >
                      <div>
                        <p className="text-xs font-medium text-[#1d1d1f] dark:text-white">
                          Terms v{record.termsVersion} / Privacy v{record.privacyVersion}
                        </p>
                        <p className="text-xs text-[#6e6e73] dark:text-[#86868b]">
                          {record.context === 'signup' ? 'Initial signup' : 'Re-accepted'}
                          {record.source && <> &middot; {record.source}</>}
                        </p>
                      </div>
                      <span className="text-xs text-[#6e6e73] dark:text-[#86868b] whitespace-nowrap">
                        {formatDate(record.acceptedAt)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Info Note */}
        <p className="text-xs text-[#86868b] dark:text-[#6e6e73] mt-3">
          By using QuickMeet, you agree to our Terms of Service and Privacy Policy.
          We may update these documents and will notify you of significant changes.
        </p>
      </div>
    </div>
  );
}
